import jsPDF from 'jspdf';
import { numberToFrenchText, formatPaymentMethod } from './numberToText.js';
import { formatDateOnlyFr } from './dateFormat.js';

// Professional PDF Invoice Generator
export const generateProfessionalInvoicePDF = (invoice, clientName, items = [], payments = [], selectedPaymentMethod = null) => {
  const doc = new jsPDF();
  
  // Page dimensions and margins
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;
  const contentWidth = pageWidth - (2 * margin);
  
  // Professional color scheme
  const primaryColor = [0, 51, 102]; // Dark blue
  const secondaryColor = [70, 130, 180]; // Steel blue
  const textColor = [51, 51, 51]; // Dark gray
  const lightGray = [245, 245, 245]; // Very light gray for backgrounds
  const borderColor = [220, 220, 220]; // Light gray for borders
  
  // Helper functions
  const cleanAmount = (amount) => {
    if (!amount) return 0;
    
    // Convert to string and remove any strange prefixes
    let cleanAmount = String(amount);
    
    // Remove any prefixes like "1/", "2/", etc.
    cleanAmount = cleanAmount.replace(/^\d+\//, '');
    
    // Remove any non-numeric characters except decimal separators
    cleanAmount = cleanAmount.replace(/[^\d,.-]/g, '');
    
    // Convert comma to dot for parsing
    cleanAmount = cleanAmount.replace(',', '.');
    
    // Parse as float
    const numAmount = parseFloat(cleanAmount);
    
    return isNaN(numAmount) ? 0 : numAmount;
  };
  
  const formatCurrency = (amount) => {
    const cleanedAmount = cleanAmount(amount);
    if (cleanedAmount === 0) return '0,00 DH';
    
    // Format with French locale (comma as decimal separator, no thousands separator)
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      style: 'decimal',
      useGrouping: false // This removes the thousands separator (space)
    }).format(cleanedAmount) + ' DH';
  };
  
  const formatDate = (dateString) => formatDateOnlyFr(dateString);
  
  const safeText = (text) => {
    if (!text) return '';
    return String(text).replace(/[^\x00-\x7F]/g, '');
  };

  const getFactureDisplayNumber = (inv) => {
    if (inv.numero_facture != null && String(inv.numero_facture).trim() !== '') {
      return String(inv.numero_facture).trim();
    }
    return inv.id;
  };

  const factureFileSlug = (inv) => {
    const n = getFactureDisplayNumber(inv);
    return String(n).replace(/[/\\:*?"<>|]/g, '_');
  };
  
  // Header Section - Clean and professional
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 45, 'F');
  
  // Company name - left aligned
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('EP OPTIQUE', margin, 18);
  
  // Invoice title - right aligned
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text('FACTURE', pageWidth - margin, 18, { align: 'right' });
  
  // Invoice details section - clean layout
  doc.setTextColor(...textColor);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('FACTURE À:', margin, 65);
  
  doc.setFontSize(13);
  doc.text(safeText(clientName), margin, 75);
  
  // Invoice number and date - properly aligned
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`FACTURE N°: ${getFactureDisplayNumber(invoice)}`, pageWidth - margin - 80, 65);
  doc.text(`DATE: ${formatDate(invoice.date || invoice.created_at)}`, pageWidth - margin - 80, 75);
  
  // Clean separator line
  doc.setDrawColor(...borderColor);
  doc.setLineWidth(0.5);
  doc.line(margin, 85, pageWidth - margin, 85);
  
  // Items table - professional design
  const tableY = 100;
  
  // Table header - clean and readable
  doc.setFillColor(...primaryColor);
  doc.rect(margin, tableY, contentWidth, 15, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('DESIGNATION', margin + 5, tableY + 10);
  doc.text('QUANTITÉ', margin + 100, tableY + 10);
  doc.text('PRIX UNIT.', margin + 150, tableY + 10);
  doc.text('MONTANT', pageWidth - margin - 50, tableY + 10);
  
  // Items content - clean formatting
  doc.setTextColor(...textColor);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  
  let yPosition = tableY + 25;
  let total = 0;
  /** Largeur max (mm) pour la colonne DESIGNATION — avant QUANTITÉ (x = margin+100) */
  const designationColumnMaxWidth = 88;
  const nameLineHeight = 4.5;
  const prescLineHeight = 4;
  
  if (items && items.length > 0) {
    items.forEach((item, index) => {
      const cleanPrice = cleanAmount(item.price || 0);
      const cleanQuantity = parseInt(item.quantity || 1);
      const itemTotal = cleanPrice * cleanQuantity;
      total += itemTotal;
      
      const rowStartY = yPosition;
      
      // Item name - remove ADD patterns from product name and extract ADD value
      let productName = item.product_name || `Produit ${item.product_id}`;
      let extractedADD = null;
      
      const addMatch = productName.match(/ADD\s*([+\-]?\d+\.?\d*)/i);
      if (addMatch && addMatch[1]) {
        extractedADD = parseFloat(addMatch[1]);
        if (isNaN(extractedADD)) {
          extractedADD = null;
        }
      }
      
      productName = productName.replace(/\s*ADD\s*[+\-]?\d+\.?\d*/gi, '').trim();
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      const nameLines = doc.splitTextToSize(safeText(productName) || ' ', designationColumnMaxWidth);
      if (nameLines.length === 0) nameLines.push('-');
      const nameBlockHeight = Math.max(nameLineHeight, nameLines.length * nameLineHeight);
      
      // Prescription layout (same logic as drawing) — pour hauteur de ligne
      const formatPrescriptionValue = (value) => {
        if (!value || value === 0) return '0.00';
        const num = parseFloat(value);
        return num > 0 ? `+${num.toFixed(2)}` : num.toFixed(2);
      };
      
      let rightData = null;
      let leftData = null;
      if (item.item_type === 'verre') {
        if (item.right_sph !== undefined && item.right_sph !== null &&
            item.left_sph !== undefined && item.left_sph !== null) {
          rightData = {
            sph: item.right_sph,
            cyl: item.right_cyl,
            axe: item.right_axe,
            addition: item.right_addition || extractedADD
          };
          leftData = {
            sph: item.left_sph,
            cyl: item.left_cyl,
            axe: item.left_axe,
            addition: item.left_addition || extractedADD
          };
        }
      }
      
      let prescLinesCount = 0;
      if (item.item_type === 'verre' && rightData && leftData) {
        prescLinesCount += 1;
        const rightAddition = rightData.addition || extractedADD;
        if (rightAddition !== null && rightAddition !== undefined && parseFloat(rightAddition) !== 0) {
          prescLinesCount += 1;
        }
        prescLinesCount += 1;
        const leftAddition = leftData.addition || extractedADD;
        if (leftAddition !== null && leftAddition !== undefined && parseFloat(leftAddition) !== 0) {
          prescLinesCount += 1;
        }
      }
      
      const prescBlockHeight = prescLinesCount * prescLineHeight;
      const gapAfterName = prescLinesCount > 0 ? 2 : 0;
      const rowPadTop = 6;
      const rowPadBottom = 2;
      const totalRowHeight =
        rowPadTop + nameBlockHeight + gapAfterName + prescBlockHeight + rowPadBottom;
      
      if (index % 2 === 0) {
        doc.setFillColor(...lightGray);
        doc.rect(margin, rowStartY - rowPadTop, contentWidth, totalRowHeight, 'F');
      }
      
      doc.setTextColor(...textColor);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      let lineY = rowStartY;
      nameLines.forEach((line) => {
        doc.text(line, margin + 5, lineY);
        lineY += nameLineHeight;
      });
      
      doc.setFont('helvetica', 'normal');
      doc.text(String(cleanQuantity), margin + 100, rowStartY);
      doc.text(formatCurrency(cleanPrice), margin + 150, rowStartY);
      doc.setFont('helvetica', 'bold');
      doc.text(formatCurrency(itemTotal), pageWidth - margin - 50, rowStartY);
      
      yPosition = lineY;
      if (prescLinesCount > 0) {
        yPosition += gapAfterName;
      }
      
      if (item.item_type === 'verre' && rightData && leftData) {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        
        const sphRight = formatPrescriptionValue(rightData.sph);
        const cylRight = formatPrescriptionValue(rightData.cyl);
        const axeRight = rightData.axe ? parseInt(rightData.axe) : 0;
        const prescriptionRight = `${sphRight} (${cylRight} ${axeRight}°)`;
        
        doc.text(safeText(`Oeil droit: ${prescriptionRight}`), margin + 15, yPosition);
        yPosition += prescLineHeight;
        
        const rightAddition = rightData.addition || extractedADD;
        if (rightAddition !== null && rightAddition !== undefined && parseFloat(rightAddition) !== 0) {
          const addition = formatPrescriptionValue(rightAddition);
          doc.text(addition, margin + 15, yPosition);
          yPosition += prescLineHeight;
        }
        
        const sphLeft = formatPrescriptionValue(leftData.sph);
        const cylLeft = formatPrescriptionValue(leftData.cyl);
        const axeLeft = leftData.axe ? parseInt(leftData.axe) : 0;
        const prescriptionLeft = `${sphLeft} (${cylLeft} ${axeLeft}°)`;
        
        doc.text(safeText(`Oeil gauche: ${prescriptionLeft}`), margin + 15, yPosition);
        yPosition += prescLineHeight;
        
        const leftAddition = leftData.addition || extractedADD;
        if (leftAddition !== null && leftAddition !== undefined && parseFloat(leftAddition) !== 0) {
          const addition = formatPrescriptionValue(leftAddition);
          doc.text(addition, margin + 15, yPosition);
          yPosition += prescLineHeight;
        }
      }
      
      yPosition += 2;
    });
  } else {
    // Simple invoice without detailed items
    const amount = cleanAmount(invoice.amount || 0);
    total = amount;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Montant de la facture', margin + 5, yPosition);
    doc.setFont('helvetica', 'bold');
    doc.text(formatCurrency(amount), pageWidth - margin - 50, yPosition);
    yPosition += 12;
  }
  
  // Payment method section - positioned right after items with smaller font
  yPosition += 8;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('ARRETEZ LA FACTURE PRESENTE A LA SOMME DE ' + numberToFrenchText(total).toUpperCase() + ' DIHRAMS', margin, yPosition);
  
  // Payment method on next line - use selected payment method or format from payments
  yPosition += 8;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  const paymentMethodText = selectedPaymentMethod || formatPaymentMethod(payments);
  doc.text('PAYE EN ' + paymentMethodText.toUpperCase(), margin, yPosition);
  
  // Reset text color
  doc.setTextColor(...textColor);
  
  // Total section - clean and professional
  yPosition += 12;
  doc.setDrawColor(...borderColor);
  doc.setLineWidth(0.5);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 12;
  
  // Total - emphasized (no TVA)
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL:', pageWidth - margin - 80, yPosition);
  doc.text(formatCurrency(total), pageWidth - margin - 20, yPosition);
  
  // Clean separator line after total
  yPosition += 8;
  doc.setDrawColor(...borderColor);
  doc.setLineWidth(0.5);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 15;
  
  // Footer - clean and minimal
  const footerY = pageHeight - 50;
  
  // Footer background
  doc.setFillColor(...lightGray);
  doc.rect(0, footerY, pageWidth, 50, 'F');
  
  // Footer border
  doc.setDrawColor(...borderColor);
  doc.setLineWidth(0.5);
  doc.line(margin, footerY, pageWidth - margin, footerY);
  
  doc.setTextColor(...textColor);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  
  // Left column
  doc.text('STE EP OPTIQUE', margin, footerY + 12);
  doc.text('IF: 50215220', margin, footerY + 19);
  doc.text('RC: 54911', margin, footerY + 26);
  doc.text('Patente N: 17101768', margin, footerY + 33);
  
  // Right column
  doc.text('ICE: 002966305000046', pageWidth - margin - 80, footerY + 12);
  doc.text('TEL: 0764200156', pageWidth - margin - 80, footerY + 19);
  doc.text('INPE: 135031136', pageWidth - margin - 80, footerY + 26);
  doc.text('Email: contact@epoptique.ma', pageWidth - margin - 80, footerY + 33);
  
  // Bottom accent bar
  doc.setFillColor(...primaryColor);
  doc.rect(0, pageHeight - 5, pageWidth, 5, 'F');
  
  // Save the PDF
  doc.save(`Facture_${factureFileSlug(invoice)}.pdf`);
};

// Generate PDF for sales orders (commandes)
export const generateOrderInvoicePDF = (order, clientName, items = [], payments = [], selectedPaymentMethod = null) => {
  return generateProfessionalInvoicePDF(order, clientName, items, payments, selectedPaymentMethod);
};

// Generate PDF for sales invoices
export const generateSalesInvoicePDF = (invoice, clientName, items = [], payments = [], selectedPaymentMethod = null) => {
  return generateProfessionalInvoicePDF(invoice, clientName, items, payments, selectedPaymentMethod);
}; 